import { isString } from 'fitify-types/src/type-guards/common'
import { CoreUser } from 'fitify-types/src/types/core/user'
import { usePromoContext } from 'fitify-ui-onboarding/src/contexts/PromoContext'
import { useSubscriptionConfig } from 'fitify-ui-onboarding/src/hooks/subscription/useSubscriptionConfig'
import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import { logEvent } from 'fitify-ui-onboarding/src/utils/analytics'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import type { MetaDataProps } from 'fitify-ui-onboarding/src/utils/meta-tags'
import { Suspense, useEffect, useMemo } from 'react'

import { useFunnel } from 'hooks/useFunnel'
import { SubscriptionPageGateway } from 'sections/subscription/SubscriptionPageGateway'
import { SubscriptionPageLoader } from 'sections/subscription/SubscriptionPageLoader'
import { useDataState } from 'utils/state'

type Props = {
  defaultPlanTitle?: string
  metaData: MetaDataProps
}

export const SubscriptionPage = ({ defaultPlanTitle, metaData }: Props) => {
  const { promoCode, isInitialized } = usePromoContext()
  const { pageConfig, subscriptionPlanConfigs } = useSubscriptionConfig({
    defaults: {
      pageConfig: { defaultPeriod: 'longest', planTitle: defaultPlanTitle },
    },
    promoCode,
  })
  const { set } = useDataState()
  const funnel = useFunnel()
  const growthBook = useGrowthBook()
  const router = useRouter()

  const gateway = useMemo(() => {
    if (!isInitialized) {
      return undefined
    }

    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Flex &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.flexPriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Flex
    }
    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Paddle &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.paddlePriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Paddle
    }

    if (
      pageConfig.gateway === CoreUser.Subscription.Gateway.Stripe &&
      subscriptionPlanConfigs.some((planConfig) =>
        isString(planConfig.stripePriceId)
      )
    ) {
      return CoreUser.Subscription.Gateway.Stripe
    }

    return CoreUser.Subscription.Gateway.Fastspring
  }, [isInitialized, pageConfig.gateway, subscriptionPlanConfigs])

  useEffect(() => {
    if (gateway) {
      // Log paywall screen
      logEvent(`paywall`, {
        promo: promoCode,
        screen: 'primary',
        gateway,
      })
    }
  }, [gateway, promoCode])

  useEffect(() => {
    const printableId = funnel
      ? growthBook.evalFeature(`${funnel}_printable`)?.value
      : undefined

    set('common', {
      printableId: isString(printableId) ? printableId : undefined,
      subscriptionFunnel: funnel,
    })
  }, [funnel, growthBook, set])

  // Handle paywall close event, if user tries to leave subscription page
  // Listen to history popstate, if event has url set by Next.js router, compare its pathname with current pathname,
  // if they are different, it means user tries to leave the page, then log paywall_close event.
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const eventUrl = event.state?.url

      if (isString(eventUrl)) {
        const [eventPathname] = eventUrl.split('?')
        if (eventPathname === router.resolvedPathname) {
          return
        }
      }

      logEvent('paywall_close')
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [router.resolvedPathname])

  return (
    <Suspense
      fallback={
        <SubscriptionPageLoader metaData={metaData} pageConfig={pageConfig} />
      }
    >
      <SubscriptionPageGateway
        gateway={gateway}
        metaData={metaData}
        pageConfig={pageConfig}
        subscriptionPlanConfigs={subscriptionPlanConfigs}
      />
    </Suspense>
  )
}
